import type { TOC } from '@ember/component/template-only';

/**
 * CardGrid — responsive card grid driven by **container queries**, not
 * viewport breakpoints. Pages don't declare `md:grid-cols-3 lg:grid-cols-4`
 * anymore; the grid reflows based on its own width, which means a CardGrid
 * inside a narrow sidebar collapses to one column while the same CardGrid
 * on a full-width page wraps to four — no per-page logic.
 *
 * Implemented with `grid-template-columns: repeat(auto-fit, minmax(..., 1fr))`
 * so the breakpoint is encoded as a **single min-column-width**. Pick the
 * preset that matches the densest content (e.g. metric cards = "narrow",
 * feature cards = "medium").
 *
 * Gap likewise scales by preset, so sidebar grids tighten automatically.
 *
 * @example
 * ```gts
 * <CardGrid @density="narrow">
 *   <StatCard ... />
 *   <StatCard ... />
 *   <StatCard ... />
 *   <StatCard ... />
 * </CardGrid>
 * ```
 */
export type CardGridDensity = 'narrow' | 'medium' | 'wide';

interface CardGridSignature {
  Args: {
    /** Density preset — sets the auto-fit min column width and gap. Default: 'medium'. */
    density?: CardGridDensity;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

const CardGrid: TOC<CardGridSignature> = <template>
  <div
    data-test-card-grid
    data-card-grid
    data-card-grid-density={{if @density @density "medium"}}
    class="app-card-grid app-card-grid--{{if @density @density 'medium'}}"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

export default CardGrid;

export type { CardGridSignature };
